/**
 * 口令派生的第一段：在客户端做。
 *
 * 为什么要分两段：Cloudflare Workers 免费版每次请求只有 10ms CPU，
 * 而抗离线爆破恰恰要靠「故意算得慢」。这两件事在服务端是直接冲突的 ——
 * 迭代次数调到有意义的强度，登录请求就会被平台掐断。
 *
 * 所以把重活挪到浏览器（没有 CPU 限制）：
 *
 *   浏览器：clientKey = PBKDF2(口令, 盐=SHA256("moonly|"+登录名), 30 万次)
 *   服务端：存的是 PBKDF2(clientKey, 每用户随机盐, 1 万次)
 *
 * 效果：
 *   · 数据库万一泄漏，攻击者每猜一次口令要付 31 万次迭代的代价，
 *     比纯服务端方案能承受的强度高得多
 *   · 服务端 CPU 只花约 1ms，稳稳落在免费额度里
 *   · 明文口令根本不出浏览器，服务端从头到尾看不到
 *
 * 盐用登录名推导而不是随机值，是为了让浏览器不必先问服务端要盐 ——
 * 那样会多一次往返，而且会泄漏「这个用户名存不存在」。
 * 每个用户名的盐互不相同，通用彩虹表就失效了；服务端第二段还有
 * 每用户独立的随机盐，所以入库的哈希依然是唯一加盐的。
 */

/**
 * 客户端迭代次数，取 OWASP 对 PBKDF2-SHA256 的推荐值。
 * 实测在这台机器上约 80ms —— 90 天才做一次，用户完全无感。
 */
export const CLIENT_ITERATIONS = 600_000

const encoder = new TextEncoder()

function bytesToBase64(bytes: Uint8Array): string {
  let s = ''
  for (const b of bytes) s += String.fromCharCode(b)
  return btoa(s)
}

/** 登录名 → 确定性盐。同一个名字永远得到同一个盐。 */
async function saltFor(loginName: string): Promise<Uint8Array> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    encoder.encode(`moonly|${loginName.trim().toLowerCase()}`),
  )
  return new Uint8Array(digest)
}

/**
 * 把口令拉伸成 clientKey。浏览器、Worker、seed 脚本都调这一个函数，
 * 保证三边算出来的值一定一致。
 */
export async function deriveClientKey(
  loginName: string,
  passphrase: string,
): Promise<string> {
  const salt = await saltFor(loginName)
  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(passphrase.normalize('NFKC')),
    'PBKDF2',
    false,
    ['deriveBits'],
  )
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      hash: 'SHA-256',
      salt: salt as unknown as BufferSource,
      iterations: CLIENT_ITERATIONS,
    },
    key,
    256,
  )
  return bytesToBase64(new Uint8Array(bits))
}
